/*
 * Boot. Clerk-gated from day one (vanilla @clerk/clerk-js, no React).
 *
 * IDENTITY IS CLERK, adapted once into `Gate` below. Everything after the gate —
 * workers, shell, plan stream — reads only from `Gate` and knows no provider.
 *
 * LOAD PERF: the two slowest startup steps — the identity SDK's network load and
 * the Backend Worker's bundle-download + spawn — run in PARALLEL, not in
 * sequence. An inline #splash (in index.html) already painted before this module
 * ran, so first paint is sub-100ms; this code just swaps it for sign-in or the
 * shell.
 *
 *   spawn workers ┐
 *                 ├─ both in flight at once ─→ signed in: mount <vitesse-app>
 *   clerk.load()  ┘                          → signed out: mount the sign-in gate
 *
 * Main thread does rendering + the session only; all network IO + state live in
 * the Backend Worker (see docs/nats-frontend-architecture.md).
 */
import { Clerk } from '@clerk/clerk-js';
import { dark } from '@clerk/themes';
import { BackendClient } from './bridge/backend-client';
import { AiClient } from '@vitesse/base-ui/ai';
import { initTheme, getResolvedTheme, onThemeChange } from '@vitesse/base-ui/theme';
import { initI18n, onLocaleChange } from '@vitesse/base-ui/i18n';
// Shared browser-chrome branding (tab title + V favicon), one source of truth in base-ui.
import {
  applyVitesseBranding,
  SPLASH_GRADIENT,
  vitesseClerkAppearance,
  fatal,
  mountSignIn,
  mountOrgGate,
  bindIdentity,
  mountOrgSwitcher,
  getSession,
  setAuthoredGrants,
  type IdentityHost,
} from '@vitesse/base-ui';
import { FleetControl } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import type { SessionPlanView } from '../core/bridge/connect-client';
// Design tokens (CSS custom properties) — from @vitesse/base-ui, loaded FIRST so
// every var(--*) below it resolves. Was an index.html <link>; now a module import.
import { session } from '../core/session';
import '@vitesse/base-ui/tokens.css';
// Agent's real SCSS — verbatim. Ported components render in LIGHT DOM with the
// agent's exact class-based markup so this styles them identically (no
// re-approximation). The React→Lit port keeps the markup + CSS, swaps the logic.
import '../agent-styles/entry.scss';
import './shell/app-shell';
import { CLERK_PUBLISHABLE_KEY } from '../core/env';
import { parseSharePath } from './share-boot';
import type { ShellIdentity } from '../core/shell-identity';

const root = document.getElementById('root')!;

/*
 * What the rest of boot needs from the identity provider. Written as a struct
 * rather than threaded through mountApp because this is the seam that keeps a
 * provider's shape out of the shell — the reason the last swap touched one
 * function instead of every call site.
 */
interface Gate {
  /** The identity snapshot source `bindIdentity`/`identityFrom` read. */
  identity: IdentityHost;
  /** What `<vitesse-app>.identity` receives — the three things the shell and the
   *  children it forwards to actually read. */
  shell: ShellIdentity;
  getToken(): Promise<string | null>;
  /** Mount Clerk's UserButton into the header slot. */
  mountUser(node: HTMLDivElement): void;
  /** Mount the org switcher (and with it RBAC + billing). */
  mountOrg(node: HTMLDivElement, backend: BackendClient): void;
}

async function main(): Promise<void> {
  initTheme(); // apply stored/auto theme before anything renders
  await initI18n(); // load stored/detected language before first render (en is bundled, others lazy)
  // A shared board is its own front door: no app, no org, just the board.
  if (parseSharePath(location.pathname)) {
    document.documentElement.classList.add('no-splash');
    const { bootShare } = await import('./share-boot');
    await bootShare(root, location.pathname, location.hash);
    return;
  }
  applyVitesseBranding('App'); // tab → "Vitesse - App" + the V favicon (shared, base-ui)
  await boot();
}

/**
 * First-time splash dwell: let users actually SEE the loading screen. On the
 * FIRST load of a tab/session (splash visible — the head script didn't add
 * `.no-splash`), hold a 2.5s minimum from splash paint before the app/sign-in
 * replaces it. In-session reloads (already splashed) skip the dwell entirely.
 */
async function splashDwell(): Promise<void> {
  if (document.documentElement.classList.contains('no-splash')) return;
  const SPLASH_MIN_MS = 2000;
  const start = (window as unknown as { __splashStart?: number }).__splashStart ?? 0;
  const remaining = SPLASH_MIN_MS - (performance.now() - start);
  if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
}

/**
 * Spawn both workers on the token callbacks.
 *
 * Overlaps the two slow boot steps. Most returning users are signed in, so the
 * speculatively-spawned worker is almost always the one we use. If signed out,
 * it idles cheaply and gets used the moment they sign in (no reload).
 *
 * BOTH callbacks must resolve to `string | null` and must NEVER throw: they are
 * the only auth either worker has, and a rejection there is not a retried
 * request but a permanently unauthenticated data plane.
 */
function spawnWorkers(
  getToken: () => Promise<string | null>,
  getFreshToken: () => Promise<string | null>,
): { backendReady: Promise<BackendClient>; aiReady: Promise<AiClient> } {
  const backendReady = BackendClient.create(getToken, getFreshToken);
  // The AI plane's own worker (second OS thread), spawned in the same parallel boot window and
  // sharing the identical token contract. Idles cheaply until the user opens the AI rail.
  // The worker entry is per-app (the bundler resolves this URL from a literal in app source);
  // everything behind it is the shared base-ui AI plane.
  const aiReady = AiClient.create(
    new Worker(new URL('../workers/ai.worker.ts', import.meta.url), { type: 'module', name: 'vitesse-ai' }),
    getToken,
    getFreshToken,
  );
  return { backendReady, aiReady };
}

/** Clerk appearance matching the current app theme. The app owns the baseTheme
 *  because base-ui imports no Clerk. */
function clerkAppearance() {
  return vitesseClerkAppearance(dark, getResolvedTheme() === 'dark');
}

async function boot(): Promise<void> {
  const clerk = new Clerk(CLERK_PUBLISHABLE_KEY);

  // Overlap the two slow boot steps: Clerk's network load and the workers' spawn.
  const clerkReady = clerk.load();

  /*
   * The token-callback boundary (host wires Clerk → the data layer). ALWAYS the
   * real Clerk session token — local dev included. Auth is real Clerk in every
   * environment (prod-parity: no mock, no bypass); the api-gateway verifies it
   * against Clerk's public JWKS (clerk.vitesseautomation.ai). This requires the
   * dev server on local.vitesseautomation.ai over HTTPS (see vite.config) so
   * Clerk's production keys load. The worker re-fetches via BackendClient's
   * refresh timer, so it never lapses.
   */
  const getToken = (): Promise<string | null> => clerk.session?.getToken() ?? Promise.resolve(null);
  // Force-fresh variant (bypasses Clerk's token cache) for the worker's
  // stale-token recovery: a reactive refresh that re-reads the cache gets the
  // same expired token and the stream re-open loops (~1.7s churn → constant
  // value-stream re-seeds that corrupt the live charts). skipCache mints anew.
  const getFreshToken = (): Promise<string | null> =>
    clerk.session?.getToken({ skipCache: true }) ?? Promise.resolve(null);

  const { backendReady, aiReady } = spawnWorkers(getToken, getFreshToken);

  try {
    await clerkReady;
  } catch (err) {
    // Never leave the user staring at a stuck splash — surface what broke.
    fatal(root, 'Auth failed to load', err);
    return;
  }

  /*
   * The Clerk singleton IS the shell's identity: it carries the membership graph
   * `identityFrom` reads, the session `getToken` the AI plane needs, and the
   * display name the header greets by. Nothing is copied onto a wrapper — Clerk
   * exposes `user` as a getter with no setter, so anything that ASSIGNS its
   * fields onto another object throws and aborts boot before the app mounts.
   */
  const shell = clerk as unknown as ShellIdentity;

  let appMounted = false;
  const sync = async (): Promise<void> => {
    if (clerk.user && !appMounted) {
      appMounted = true;
      const [backend, ai] = await Promise.all([backendReady, aiReady]);
      await Promise.all([backend.refreshToken(), ai.refreshToken()]); // push the now-available JWT into both workers
      mountApp(
        {
          identity: clerk as unknown as IdentityHost,
          shell,
          getToken,
          // Clerk's own UserButton — the profile card, the sign-out, the account
          // management, all of it prebuilt and themed with the app's appearance.
          mountUser: (el) => {
            try {
              clerk.unmountUserButton(el);
            } catch {
              /* not mounted yet */
            }
            try {
              clerk.mountUserButton(el, { appearance: clerkAppearance(), userProfileProps: { appearance: clerkAppearance() } });
            } catch {
              /* Clerk not loaded (dev HTTP preview) — UserButton is cosmetic, skip it */
            }
          },
          // Clerk's OrganizationSwitcher, with the permissions + billing pages
          // riding inside its OrganizationProfile — see base-ui mountOrgSwitcher.
          mountOrg: (node, backendClient) =>
            mountOrgSwitcher(clerk as unknown as Parameters<typeof mountOrgSwitcher>[0], node, {
              appearance: clerkAppearance(),
              ...orgSwitcherOptions(backendClient, getToken),
            }),
        },
        backend,
        ai,
      );
    } else if (clerk.user && !clerk.organization && !appMounted) {
      /*
       * Signed in, but in no organization yet — a new customer, or someone whose
       * invitation has not been accepted.
       *
       * Handled HERE rather than left to the provider, which would redirect to
       * its hosted account portal: a person's first minute with the product
       * should not be spent on a differently-branded domain. Same component,
       * same brand fade, inside the app.
       */
      mountOrgGate(root, clerk as unknown as Parameters<typeof mountOrgGate>[1], {
        background: SPLASH_GRADIENT,
        darkTheme: dark,
      });
    } else if (!clerk.user && appMounted) {
      location.reload(); // signed out → clean teardown of worker + state
    } else if (!clerk.user) {
      // The brand fade behind Clerk's card; index.html's #splash paints the same one.
      mountSignIn(root, clerk as unknown as Parameters<typeof mountSignIn>[1], {
        background: SPLASH_GRADIENT,
        darkTheme: dark,
      });
    }
  };

  await splashDwell();
  clerk.addListener(() => void sync());
  await sync();
}

/* ------------------------------------------------------------------ shared */

/*
 * The org switcher, permissions page included — see base-ui/mountOrgSwitcher.
 * This app supplies only the write, and it goes through the backend worker
 * because that is where its IO lives (Cardinal Rule: the main thread holds no
 * transport).
 */
function orgSwitcherOptions(backend: BackendClient, getToken: () => Promise<string | null>) {
  return {
    /*
     * `=== true`, not `!== false`, and the difference is the whole gate.
     *
     * The old spelling read "allowed unless explicitly refused", so an absent
     * session — the plan has not arrived, the org just switched, boot is still in
     * flight — evaluated to ALLOWED. An org on a tier without custom RBAC was
     * offered the editor, and only found out when the server refused the save.
     *
     * Unknown must mean no. The cost is a control that appears a moment late; the
     * alternative is offering an action that cannot succeed, to exactly the people
     * who have not paid for it.
     */
    canEdit: () => getSession()?.allowsRpc(FleetControl.method.setUserGrants) === true,
    // Its OWN annotation, not the neighbouring one: authoring roles is a
    // different RPC selling a different tier, so borrowing the answer would
    // offer a Save the server refuses.
    canEditRoles: () => getSession()?.allowsRpc(FleetControl.method.setOrgRoles) === true,
    save: (userId: string, payload: Parameters<BackendClient['api']['setUserGrants']>[1]) =>
      backend.api.setUserGrants(userId, payload),
    // THE ROSTER, from the server rather than the SDK's membership list: it is
    // the same list the grants are authored against, so the editor cannot show a
    // person the write path does not know about.
    members: () => backend.api.listOrgMembers(),
    roles: () => backend.api.listOrgRoles(),
    saveRoles: (roles: Parameters<BackendClient['api']['setOrgRoles']>[0]) => backend.api.setOrgRoles(roles),
    // The same session this mount was handed; the app's own getToken lives in the boot fn.
    billing: { getToken },
    // The org's history. Read through the backend worker like every other call —
    // the main thread holds no transport.
    audit: { events: () => backend.api.listAuditEvents() },
  };
}

function mountApp(gate: Gate, backend: BackendClient, ai: AiClient): void {
  // OAuth returns to a callback URL the IdP then hands off from. The app routes
  // via state, not the URL hash, so strip any leftover auth hash for a clean URL
  // — in place, no reload. (OAuth returns to #/sso-callback, which Clerk consumes
  // before handing off here; this strips whatever is left.)
  if (location.hash) {
    history.replaceState(null, '', location.pathname + location.search);
  }
  root.replaceChildren();
  const app = document.createElement('vitesse-app');
  app.backend = backend;
  app.ai = ai;
  app.identity = gate.shell;

  // Identity now, and again on every Clerk tick — Session compares by value, so a
  // token refresh that changes nothing does not re-render the app.
  bindIdentity(gate.identity);

  // The plan, from the same api-gateway stream the gate is served from, so a
  // greyed control and a refused call derive from one fact and cannot disagree.
  void subscribePlan(backend);

  // The Clerk UserButton must live in the LIGHT DOM (Clerk injects CSS into
  // document.head, which can't reach shadow DOM). We hand the node to <vitesse-app>
  // which renders it INSIDE <vitesse-header> with slot="user", so the header's real
  // shadow <slot name="user"> projects it into the right-aligned actions (it stays
  // light-DOM, so Clerk's CSS still reaches it). Appending it to <vitesse-app>
  // directly — a light-DOM host with no shadow slot — dropped it at top-left.
  // Re-mount on theme change to swap the baseTheme.
  const userDiv = document.createElement('div');
  userDiv.slot = 'user';
  app.userEl = userDiv;
  // Org switcher — same light-DOM + slot projection as the UserButton. The
  // platform is org-scoped end-to-end (the api-gateway rejects a no-org token),
  // so hidePersonal forces an active org context (no personal-account mode).
  const orgDiv = document.createElement('div');
  orgDiv.slot = 'org';
  app.orgEl = orgDiv;
  root.appendChild(app);

  const mountUser = () => gate.mountUser(userDiv);
  const mountOrg = () => gate.mountOrg(orgDiv, backend);

  mountUser();
  mountOrg();
  // Re-mount both light-DOM Clerk widgets on theme change (swap baseTheme) and on
  // a language switch (the shell re-renders and would otherwise drop them).
  onThemeChange(() => {
    mountUser();
    mountOrg();
  });
  onLocaleChange(() => {
    mountUser();
    mountOrg();
  });

  // The Clerk trigger keeps focus (and its ring) after the popover closes on an
  // outside click. Blur it on any pointerdown that isn't on the button or its
  // popover, so the ring auto-clears once you click away.
  document.addEventListener('pointerdown', (e) => {
    const onClerk = e
      .composedPath()
      .some((n) => n instanceof Element && (n.closest('.cl-userButtonTrigger') || n.closest('.cl-userButtonPopoverCard')));
    if (!onClerk) userDiv.querySelector<HTMLElement>('.cl-userButtonTrigger')?.blur();
  });
}

void main();

/**
 * Feed `session` from SessionEntitlements.Subscribe.
 *
 * Failure is deliberately quiet: the plan is a rendering hint, the server enforces
 * regardless, and a UI that refused to start because entitlements were briefly
 * unreachable would turn a cosmetic outage into a total one. Session stays
 * plan-unknown, which reads as fail-open.
 */
async function subscribePlan(backend: BackendClient): Promise<void> {
  try {
    const port = await backend.api.subscribeSessionPlan('');
    port.onmessage = (e: MessageEvent<{ kind: string; items?: SessionPlanView[] }>) => {
      if (e.data.kind === 'snapshot' && e.data.items?.length) {
        const frame = e.data.items[0];
        session.setPlan(frame);
        // The authored permission matrix rides the same frame. Absent means the
        // store could not say — keep the last picture, never clear it.
        if (frame.authored !== undefined) setAuthoredGrants(frame.orgId, frame.authored);
      }
    };
    port.start();
  } catch {
    // plan stays unknown → capability checks pass → the server still decides
  }
}
